import React from 'react';
import { Link } from 'react-router-dom';
import { toProfessionalWording } from '../utils/askAiWording';

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

function rewriteAskAiHref(raw: string): string | null {
  const href = String(raw || '').trim();
  if (!href) return null;
  if (/^(https?:\/\/|mailto:|tel:)/i.test(href)) return href;
  if (!href.startsWith('/')) return null;

  const appointment = href.match(new RegExp(`^/appointments/(${UUID})(/deposit(?:/complete)?)?/?$`, 'i'));
  if (appointment) {
    return `/dashboard/customer/appointments/${appointment[1]}${appointment[2] || ''}`;
  }
  const reschedule = href.match(new RegExp(`^/reschedule/(${UUID})/?$`, 'i'));
  if (reschedule) return `/dashboard/customer/reschedule/${reschedule[1]}`;
  const quote = href.match(new RegExp(`^/(?:quote-requests|quotes)/(${UUID})/?$`, 'i'));
  if (quote) return `/dashboard/customer/quote-requests/${quote[1]}`;
  const invoice = href.match(new RegExp(`^/invoices/(${UUID})(/pay(?:/complete)?)?/?$`, 'i'));
  if (invoice) return `/dashboard/customer/invoices/${invoice[1]}${invoice[2] || ''}`;
  if (href === '/appointments') return '/dashboard/customer/appointments';
  if (href === '/quotes' || href === '/quote-requests') return '/dashboard/customer/quote-requests';
  if (href === '/invoices') return '/dashboard/customer/invoices';
  if (href === '/profile') return '/dashboard/customer/profile';
  return href;
}

function isSafeHref(href: string): boolean {
  return /^(https?:\/\/|mailto:|tel:|\/)/i.test(href) && !/^javascript:/i.test(href);
}

function isInternalPath(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

type Piece = { type: 'text'; value: string } | { type: 'link'; label: string; href: string };

function tokenize(text: string): Piece[] {
  const pieces: Piece[] = [];
  const pattern = /\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s)<>]+)/g;
  let last = 0;
  let match: RegExpExecArray | null = pattern.exec(text);
  while (match) {
    if (match.index > last) pieces.push({ type: 'text', value: text.slice(last, match.index) });
    if (match[1] != null) {
      pieces.push({ type: 'link', label: match[1], href: match[2] });
    } else {
      pieces.push({ type: 'link', label: match[3], href: match[3] });
    }
    last = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (last < text.length) pieces.push({ type: 'text', value: text.slice(last) });
  return pieces;
}

function renderPieces(text: string, accentColor: string, keyPrefix: string) {
  const style = { color: accentColor, fontWeight: 600, textDecoration: 'underline' } as const;
  return tokenize(text).map((piece, index) => {
    const key = `${keyPrefix}-${index}`;
    if (piece.type === 'text') return <React.Fragment key={key}>{piece.value}</React.Fragment>;
    const href = rewriteAskAiHref(piece.href);
    if (!href || !isSafeHref(href)) return <React.Fragment key={key}>{piece.label}</React.Fragment>;
    if (isInternalPath(href)) {
      return (
        <Link key={key} to={href} style={style}>
          {piece.label}
        </Link>
      );
    }
    return (
      <a key={key} href={href} target="_blank" rel="noopener noreferrer" style={style}>
        {piece.label}
      </a>
    );
  });
}

function isServiceListLine(line: string): boolean {
  return /^\s*[-•]\s+\S/.test(line);
}

type Block = { kind: 'text'; value: string } | { kind: 'list'; items: string[] };

function blocksFrom(text: string): Block[] {
  const blocks: Block[] = [];
  for (const chunk of text.split(/\n{2,}/)) {
    const value = chunk.replace(/\r/g, '').trim();
    if (!value) continue;
    const lines = value.split('\n');
    if (lines.every((line) => isServiceListLine(line))) {
      blocks.push({
        kind: 'list',
        items: lines.map((line) => line.replace(/^\s*[-•]\s+/, '').trim()).filter(Boolean),
      });
      continue;
    }
    blocks.push({ kind: 'text', value });
  }
  return blocks;
}

const AskAiRichText: React.FC<{
  text: string;
  accentColor?: string;
}> = ({ text, accentColor = '#0B4778' }) => {
  const blocks = blocksFrom(toProfessionalWording(String(text || '')));
  if (blocks.length === 0) return <p className="m-0 whitespace-pre-wrap" />;
  return (
    <div className="flex flex-col gap-2">
      {blocks.map((block, index) => {
        if (block.kind === 'list') {
          return (
            <ul key={index} className="m-0 list-disc space-y-1 pl-5">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex} className="whitespace-pre-wrap pl-0.5">
                  {renderPieces(item, accentColor, `${index}-${itemIndex}`)}
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={index} className="m-0 whitespace-pre-wrap">
            {renderPieces(block.value, accentColor, String(index))}
          </p>
        );
      })}
    </div>
  );
};

export default AskAiRichText;
